import React, { useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import PersonalSpace from '../../../../../molecules/PersonalSpace'
import ScrollContent from '../../../../../molecules/ScrollContent'
import DenaliLogo from '../../../../../../apps/aiApp/Assistant/common/DenaliLogo'
import { useAgentPanelContext } from '../AgentPanelContext'
import { useRotatingMessage } from '../hooks/useRotatingMessage'
import { isRenderableMessage } from '../message/messageUtils'
import ChatMessage from './ChatMessage'

const GENERIC_MESSAGES = new Set(['Queued request...', 'Thinking...'])

const useStyles = makeStyles(() => ({
  thinkingIndicator: {
    border: 'var(--border-neutral-thin)',
    background: 'var(--surface-bg-color-d1)',
    color: 'var(--color-ai-700)'
  },
  thinkingLogo: {
    width: '18px',
    height: '18px',
    animation: '$logoSpin 1.2s linear infinite'
  },
  thinkingText: {
    transition: 'opacity 0.3s ease'
  },
  '@keyframes logoSpin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  }
}))

function ChatMessages () {
  const classes = useStyles()
  const { messages, agentStatus, processingMessage, isThreadLoading } = useAgentPanelContext()

  const scrollRef = useRef(null)
  const lastMessageRef = useRef(null)
  const prevMessageCount = useRef(0)
  const isProcessing = agentStatus === 'processing'
  const showThinkingIndicator = isProcessing || isThreadLoading

  const renderableMessages = useMemo(
    () => messages.filter(isRenderableMessage),
    [messages]
  )

  const toolOverride = processingMessage && !GENERIC_MESSAGES.has(processingMessage)
    ? processingMessage
    : null
  const displayMessage = useRotatingMessage(showThinkingIndicator, toolOverride)

  useEffect(() => {
    if (renderableMessages.length > prevMessageCount.current) {
      const target = lastMessageRef.current
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
    prevMessageCount.current = renderableMessages.length
  }, [renderableMessages.length])

  return (
    <ScrollContent ref={scrollRef} className='px-4 py-5 flex-1'>
      <div className='flex flex-col gap-md'>
        {renderableMessages.map((message, index) => (
          <div key={`message-${index}`} ref={index === renderableMessages.length - 1 ? lastMessageRef : undefined}>
            <ChatMessage message={message} />
          </div>
        ))}
        {showThinkingIndicator && (
          <div className={`${classes.thinkingIndicator} flex items-center gap-2 radius-md p-2 font-size-xs`}>
            <div className={classes.thinkingLogo}>
              <DenaliLogo size='18px' alt='Loading updates' />
            </div>
            <span className={`${classes.thinkingText} min-w-0`}>{displayMessage}</span>
          </div>
        )}
      </div>
      <PersonalSpace size={35} />
    </ScrollContent>
  )
}

export default ChatMessages
